import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { formatCurrency } from '../mocks/wipAiFixture'

const Card = styled.div`
  margin-top: 28px;
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 12px;
  box-shadow: ${p => p.theme.colors.semantic.shadowSm};
  padding: 18px 20px;
`

const RowTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`

const SectionTitle = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.semantic.textPrimary};
`

const SectionSub = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.semantic.textMuted};
  margin-top: 4px;
`

const Legend = styled.div`
  display: flex;
  gap: 14px;
  font-size: 11px;
  color: ${p => p.theme.colors.semantic.textMuted};
  margin-top: 4px;
  i {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 2px;
    margin-right: 5px;
    vertical-align: middle;
  }
`

const PacingRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 18px;
  align-items: start;
  margin-top: 8px;
`

const Spark = styled.div`
  padding: 12px 14px;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SparkM = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.semantic.textMuted};
  font-weight: 700;
`

const SparkN = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`

const SparkP = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.semantic.textMuted};
`

const WipAiPacingChart = ({ weekly, history, iso, title }) => {
  const weeks = weekly || []
  const vals = weeks.map(w => Math.max(w.target || 0, w.actual || 0))
  const max = (vals.length ? Math.max(...vals) : 0) * 1.15 || 1
  const W = 560
  const H = 180
  const pad = { t: 10, r: 10, b: 32, l: 48 }
  const innerW = W - pad.l - pad.r
  const innerH = H - pad.t - pad.b
  const barGroupW = innerW / Math.max(weeks.length, 1)
  const barW = barGroupW * 0.32
  const ticks = [0, max * 0.25, max * 0.5, max * 0.75, max].map(v => ({
    v,
    y: pad.t + innerH - (v / max) * innerH,
  }))
  return (
    <Card id="pacing">
      <RowTop>
        <div>
          <SectionTitle>{title}</SectionTitle>
          <SectionSub>
            Actual vs. weekly budget target, with trailing three months for context.
          </SectionSub>
        </div>
        <Legend>
          <span>
            <i style={{ background: '#EFEFF3' }} />
            Target
          </span>
          <span>
            <i style={{ background: '#4241BF' }} />
            Actual
          </span>
          <span>
            <i style={{ background: '#D0342C', borderRadius: '50%' }} />
            Over
          </span>
        </Legend>
      </RowTop>

      <PacingRow>
        <div style={{ minWidth: 0 }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 180, marginTop: 10 }}>
            {ticks.map((t, i) => (
              <g key={i}>
                <line
                  x1={pad.l}
                  x2={W - pad.r}
                  y1={t.y}
                  y2={t.y}
                  stroke="#E5E5EC"
                  strokeDasharray="2 3"
                />
                <text
                  x={pad.l - 8}
                  y={t.y + 3}
                  textAnchor="end"
                  fontFamily="Arial"
                  fontSize="9"
                  fill="#676767"
                >
                  {`$${Math.round(t.v / 1000)}k`}
                </text>
              </g>
            ))}
            {weeks.map((w, i) => {
              const x = pad.l + barGroupW * i + barGroupW / 2
              const targetH = (w.target / max) * innerH
              const actualH = (w.actual / max) * innerH
              const over = w.actual > w.target
              return (
                <g key={i}>
                  <rect
                    x={x - barW - 2}
                    y={pad.t + innerH - targetH}
                    width={barW}
                    height={targetH}
                    fill="#EFEFF3"
                    rx="2"
                  />
                  <rect
                    x={x + 2}
                    y={pad.t + innerH - actualH}
                    width={barW}
                    height={actualH}
                    fill={over ? '#D0342C' : '#4241BF'}
                    rx="2"
                  />
                  <text
                    x={x}
                    y={H - 14}
                    textAnchor="middle"
                    fontFamily="Arial"
                    fontSize="9"
                    fill="#676767"
                  >
                    {w.week}
                  </text>
                  <text
                    x={x + 2 + barW / 2}
                    y={pad.t + innerH - actualH - 4}
                    textAnchor="middle"
                    fontFamily="Arial"
                    fontSize="9"
                    fontWeight="700"
                    fill={over ? '#D0342C' : '#4241BF'}
                  >
                    {`$${Math.round(w.actual / 1000)}k`}
                  </text>
                </g>
              )
            })}
          </svg>
        </div>
        {(history || []).map((h, i) => (
          <Spark key={i}>
            <SparkM>{h.mLabel}</SparkM>
            <SparkN>{formatCurrency(h.total, iso)}</SparkN>
            <SparkP>
              {h.pacing}% of {formatCurrency(h.budget, iso)}
            </SparkP>
            <svg
              viewBox="0 0 80 28"
              preserveAspectRatio="none"
              style={{ width: '100%', height: 28, marginTop: 4 }}
            >
              {(() => {
                const d = h.sparks || []
                const mx = Math.max(...d, 1)
                const denom = Math.max(d.length - 1, 1)
                const pts = d
                  .map((v, j) => `${(j / denom) * 80},${28 - (v / mx) * 24 - 2}`)
                  .join(' ')
                return (
                  <>
                    <polyline points={pts} fill="none" stroke="#4241BF" strokeWidth="1.5" />
                    <polyline points={`${pts} 80,28 0,28`} fill="#EEEEFF" opacity="0.7" />
                  </>
                )
              })()}
            </svg>
          </Spark>
        ))}
      </PacingRow>
    </Card>
  )
}

WipAiPacingChart.propTypes = {
  weekly: PropTypes.arrayOf(PropTypes.object).isRequired,
  history: PropTypes.arrayOf(PropTypes.object).isRequired,
  iso: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
}

export default WipAiPacingChart
